import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, CircularProgress, Fab, LinearProgress, ToggleButton, ToggleButtonGroup, Stack, Button, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { Page, PageTitle } from '../../components';
import useFetch from '../../hooks/useFetch';
import AccountCards from './AccountCards';
import CalendarGrid from './CalendarGrid';
import AddMenu from './AddMenu';
import CheckSheet from './CheckSheet';
import ToConfirm from './ToConfirm';
import { placeOutflows } from './placeOutflows';
import { sumSetAside } from './setAside';
import { todayET, weekDays, monthWeeks, addDays, addMonths, formatWeekRange, formatMonthLabel, nextBankDay } from './dates';

export default function Calendar({ auth, setAuth, now }) {
  const { t, i18n } = useTranslation();
  const { fetchResource, showAlert, alertMessage } = useFetch({ setAuth });
  const today = useMemo(() => todayET(now), [now]);
  const [mode, setMode] = useState('week');
  const [anchor, setAnchor] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  const [refreshing, setRefreshing] = useState({});
  const [refreshErrors, setRefreshErrors] = useState({});
  const [addDate, setAddDate] = useState(null);
  const [selected, setSelected] = useState(null);
  const [toConfirmKey, setToConfirmKey] = useState(0);
  const [setAside, setSetAside] = useState(() => new Set());
  const [bankAccountId, setBankAccountId] = useState(() => {
    try {
      const saved = localStorage.getItem('calendar.bankAccountId');
      return saved ? Number(saved) : null;
    } catch {
      return null;
    }
  });
  const perms = auth.perms || {};
  const operatingAccounts = (data?.accounts || []).filter(a => a.role === 'operating');
  const weeks = mode === 'week' ? [weekDays(anchor)] : monthWeeks(anchor);
  const visible = weeks.flat();
  const from = visible[0];
  const to = visible.at(-1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetchResource({ method: 'GET', resource: `calendar?from=${from}&to=${to}`, loadingKey: 'calendar' });
      if (!r.error) setData(r);
    } finally {
      setLoading(false);
    }
  }, [fetchResource, from, to]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!data) return;
    if (bankAccountId != null && operatingAccounts.some(a => a.id === bankAccountId)) return;
    setBankAccountId(operatingAccounts[0]?.id ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const selectBank = id => {
    setBankAccountId(id);
    try { localStorage.setItem('calendar.bankAccountId', String(id)); } catch { /* no-op */ }
  };

  const refresh = async id => {
    setRefreshErrors(e => ({ ...e, [id]: null }));
    setRefreshing(r => ({ ...r, [id]: true }));
    const r = await fetchResource({ method: 'POST', resource: `calendar/refresh/${id}`, loadingKey: `refresh-${id}` });
    setRefreshing(rs => ({ ...rs, [id]: false }));
    if (r.error) return setRefreshErrors(e => ({ ...e, [id]: r.error }));
    if (r.forecast?.busy) return setBusy(b => ({ ...b, [id]: true }));
    setBusy(b => ({ ...b, [id]: false }));
    if (r.forecast) {
      const forecast = r.forecast.fresh ? { ...r.forecast, previewAt: new Date().toISOString() } : r.forecast;
      setData(d => ({ ...d, forecasts: { ...d.forecasts, [id]: forecast } }));
    }
  };

  const rawItemsByDate = data ? placeOutflows({ outflows: data.outflows, days: data.days, today, visibleDates: visible }) : {};
  const itemsByDate = {};
  for (const [date, items] of Object.entries(rawItemsByDate)) {
    itemsByDate[date] = items.filter(i => i.bank_account_id == null || i.bank_account_id === bankAccountId);
  }
  const toggleSetAside = item => setSetAside(s => {
    const next = new Set(s);
    if (next.has(item.id)) next.delete(item.id);
    else next.add(item.id);
    return next;
  });
  const setAsideTotal = sumSetAside(Object.values(itemsByDate).flat(), setAside);
  const nextBusinessDay = data?.nextBusinessDay || nextBankDay(today);
  const knownItems = data?.tomorrow ? (data.tomorrow[bankAccountId] || []) : (itemsByDate[nextBusinessDay] || []);
  const stepBack = () => setAnchor(a => (mode === 'month' ? addMonths(a, -1) : addDays(a, -7)));
  const stepForward = () => setAnchor(a => (mode === 'month' ? addMonths(a, 1) : addDays(a, 7)));

  return (
    <Page>
      <PageTitle>{t('calendar.title')}</PageTitle>
      {showAlert && <Alert severity={alertMessage.type} variant='filled'>{alertMessage.message}</Alert>}
      {operatingAccounts.length > 0 && (
        <ToggleButtonGroup size='small' exclusive value={bankAccountId} onChange={(e, v) => v != null && selectBank(v)} sx={{ mb: 1 }}>
          {operatingAccounts.map(a => <ToggleButton key={a.id} value={a.id}>{a.nickname}</ToggleButton>)}
        </ToggleButtonGroup>
      )}
      {data && (
        <AccountCards accounts={data.accounts} forecasts={data.forecasts} bankAccountId={bankAccountId} onRefresh={refresh} busy={busy} perms={perms}
          today={today} nextBusinessDay={nextBusinessDay} knownItems={knownItems} refreshing={refreshing} refreshErrors={refreshErrors}
          outflows={data.outflows} setAside={setAside} onToggleSetAside={toggleSetAside} />
      )}
      {perms.CAL_ENTER && <ToConfirm setAuth={setAuth} onSaved={load} accounts={data?.accounts || []} refreshKey={toConfirmKey} />}
      <Stack direction='row' spacing={1} sx={{ mb: 1 }} alignItems='center'>
        <Button onClick={stepBack}>‹</Button>
        <Typography variant='subtitle1' sx={{ minWidth: 160, textAlign: 'center' }}>
          {mode === 'month' ? formatMonthLabel(anchor, i18n.language) : formatWeekRange(weeks[0], i18n.language)}
        </Typography>
        <Button onClick={stepForward}>›</Button>
        <Button onClick={() => setAnchor(today)}>{t('calendar.today')}</Button>
        <ToggleButtonGroup size='small' exclusive value={mode} onChange={(e, v) => v && setMode(v)}>
          <ToggleButton value='week'>{t('calendar.week')}</ToggleButton>
          <ToggleButton value='month'>{t('calendar.month')}</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      {loading && !data && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}
      {loading && data && <LinearProgress sx={{ mb: 1 }} />}
      {data && (
        <CalendarGrid weeks={weeks} today={today} itemsByDate={itemsByDate} accounts={data.accounts} weekTotals={data.weekTotals} bankAccountId={bankAccountId}
          onDayClick={perms.CAL_ENTER ? setAddDate : undefined} onItemClick={setSelected}
          setAside={setAside} onToggleSetAside={toggleSetAside} setAsideTotal={setAsideTotal} />
      )}
      {perms.CAL_ENTER && (
        <Fab color='primary' aria-label={t('calendar.add')} onClick={() => setAddDate(today)} sx={{ position: 'fixed', bottom: 24, right: 24 }}>
          <AddIcon />
        </Fab>
      )}
      {addDate && (
        <AddMenu date={addDate} accounts={data?.accounts || []} setAuth={setAuth}
          onClose={() => { setAddDate(null); setToConfirmKey(k => k + 1); }}
          onSaved={() => { setAddDate(null); load(); setToConfirmKey(k => k + 1); }} />
      )}
      {selected && <CheckSheet item={selected} perms={perms} setAuth={setAuth} onClose={() => setSelected(null)} onSaved={() => { setSelected(null); load(); }} />}
    </Page>
  );
}
